<script setup lang="ts">
import { computed } from 'vue';

interface ColorToken {
  light: string;
  dark: string | null;
}

const props = defineProps<{
  background: ColorToken;
  foreground: ColorToken;
  foregroundMuted: ColorToken;
}>();

function hexToLinear(hex: string): number[] | null {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return null;
  return [match[1], match[2], match[3]].map((c) => {
    const v = parseInt(c, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
}

function relativeLuminance(hex: string): number | null {
  const rgb = hexToLinear(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(hexA: string, hexB: string): number | null {
  const lumA = relativeLuminance(hexA);
  const lumB = relativeLuminance(hexB);
  if (lumA === null || lumB === null) return null;
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
}

/* ── Suggestion de couleur : garde la teinte/saturation du choix de
 * l'admin, ajuste seulement la clarté (HSL L) jusqu'à atteindre le ratio
 * WCAG minimum contre ce fond précis. Deux sens possibles (plus clair vers
 * le blanc, plus sombre vers le noir) ; on part du sens qui s'éloigne le
 * moins de la couleur choisie (celui déjà du bon côté du fond), et on
 * bascule sur l'autre s'il ne suffit jamais à atteindre le ratio (recherche
 * dichotomique sur L, 12 itérations ~= précision de 0.02%). */
function hexToHsl(hex: string): [number, number, number] | null {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return null;
  const r = parseInt(match[1], 16) / 255;
  const g = parseInt(match[2], 16) / 255;
  const b = parseInt(match[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function hslToHex(h: number, s: number, l: number): string {
  if (s === 0) {
    const v = Math.round(l * 255);
    const hx = v.toString(16).padStart(2, '0');
    return `#${hx}${hx}${hx}`;
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hue2rgb = (t: number) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  const toHex = (v: number) =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(hue2rgb(h + 1 / 3))}${toHex(hue2rgb(h))}${toHex(hue2rgb(h - 1 / 3))}`;
}

function suggestAccessibleColor(
  fgHex: string,
  bgHex: string,
  minRatio: number,
): string | null {
  const hsl = hexToHsl(fgHex);
  const bgLum = relativeLuminance(bgHex);
  if (!hsl || bgLum === null) return null;
  const [h, s] = hsl;

  function ratioAtL(l: number): number {
    const candidateLum = relativeLuminance(hslToHex(h, s, l)) ?? 0;
    const lighter = Math.max(candidateLum, bgLum);
    const darker = Math.min(candidateLum, bgLum);
    return (lighter + 0.05) / (darker + 0.05);
  }

  // Le fond est clair (L élevé côté fond) → viser du texte plus sombre
  // (L → 0) donne un meilleur ratio, et inversement — trié par la
  // luminosité effective du fond, pas juste la position actuelle du texte,
  // pour choisir le bon sens même quand la couleur de départ échoue déjà
  // largement.
  const towardsBlack = ratioAtL(0) >= minRatio;
  const towardsWhite = ratioAtL(1) >= minRatio;
  if (!towardsBlack && !towardsWhite) return null; // aucun L n'atteint le ratio pour cette teinte/saturation

  // Choisit le sens le plus proche de la clarté d'origine parmi ceux
  // atteignables, pour rester visuellement proche du choix de l'admin.
  const goDark = towardsBlack && (!towardsWhite || hsl[2] <= 0.5);

  let lo = goDark ? 0 : hsl[2];
  let hi = goDark ? hsl[2] : 1;
  for (let i = 0; i < 12; i++) {
    const mid = (lo + hi) / 2;
    const meets = ratioAtL(mid) >= minRatio;
    if (goDark) {
      if (meets) lo = mid;
      else hi = mid;
    } else {
      if (meets) hi = mid;
      else lo = mid;
    }
  }
  const finalL = goDark ? lo : hi;
  return hslToHex(h, s, finalL);
}

const WCAG_AA_TEXT = 4.5;
const WCAG_AA_MUTED = 3;

interface ContrastCheck {
  label: string;
  mode: string;
  ratio: number;
  min: number;
  fg: string;
  bg: string;
  target: 'foreground' | 'foregroundMuted';
  themeMode: 'light' | 'dark';
}

const checks = computed<ContrastCheck[]>(() => {
  const results: ContrastCheck[] = [];
  const pairs: {
    label: string;
    bg: string | null;
    fg: string | null;
    min: number;
    mode: string;
    target: ContrastCheck['target'];
    themeMode: ContrastCheck['themeMode'];
  }[] = [
    {
      label: 'Titre produit',
      bg: props.background.light,
      fg: props.foreground.light,
      min: WCAG_AA_TEXT,
      mode: 'clair',
      target: 'foreground',
      themeMode: 'light',
    },
    {
      label: 'Titre produit',
      bg: props.background.dark ?? props.background.light,
      fg: props.foreground.dark ?? props.foreground.light,
      min: WCAG_AA_TEXT,
      mode: 'sombre',
      target: 'foreground',
      themeMode: 'dark',
    },
    {
      label: 'Description produit',
      bg: props.background.light,
      fg: props.foregroundMuted.light,
      min: WCAG_AA_MUTED,
      mode: 'clair',
      target: 'foregroundMuted',
      themeMode: 'light',
    },
    {
      label: 'Description produit',
      bg: props.background.dark ?? props.background.light,
      fg: props.foregroundMuted.dark ?? props.foregroundMuted.light,
      min: WCAG_AA_MUTED,
      mode: 'sombre',
      target: 'foregroundMuted',
      themeMode: 'dark',
    },
  ];

  for (const pair of pairs) {
    if (!pair.bg || !pair.fg) continue;
    const ratio = contrastRatio(pair.bg, pair.fg);
    if (ratio === null) continue;
    results.push({
      label: pair.label,
      mode: pair.mode,
      ratio,
      min: pair.min,
      fg: pair.fg,
      bg: pair.bg,
      target: pair.target,
      themeMode: pair.themeMode,
    });
  }
  return results;
});

interface FailingCheck extends ContrastCheck {
  suggestion: string | null;
}

const failingChecks = computed<FailingCheck[]>(() =>
  checks.value
    .filter((c) => c.ratio < c.min)
    .map((c) => ({
      ...c,
      suggestion: suggestAccessibleColor(c.fg, c.bg, c.min),
    })),
);

const emit = defineEmits<{
  'apply-suggestion': [
    target: ContrastCheck['target'],
    themeMode: ContrastCheck['themeMode'],
    color: string,
  ];
}>();
</script>

<template>
  <div v-if="failingChecks.length" class="tcw">
    <i class="pi pi-exclamation-triangle tcw__icon" />
    <div class="tcw__body">
      <p class="tcw__title">Contraste insuffisant</p>
      <ul class="tcw__list">
        <li v-for="(c, i) in failingChecks" :key="i" class="tcw__item">
          <span>
            {{ c.label }} en mode {{ c.mode }} : ratio
            {{ c.ratio.toFixed(2) }}:1 (minimum recommandé {{ c.min }}:1)
          </span>
          <span v-if="c.suggestion" class="tcw__suggestion">
            <span
              class="tcw__swatch"
              :style="{ background: c.suggestion }"
              aria-hidden="true"
            />
            <span class="tcw__suggestion-hex">{{ c.suggestion }}</span>
            <button
              type="button"
              class="tcw__apply-btn"
              @click="
                emit('apply-suggestion', c.target, c.themeMode, c.suggestion)
              "
            >
              Utiliser cette couleur
            </button>
          </span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
.tcw {
  display: flex;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
}
.tcw__icon {
  color: #f59e0b;
  font-size: 1.125rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}
.tcw__title {
  margin: 0 0 0.25rem;
  font-weight: 700;
  font-size: 0.8125rem;
  color: #b45309;
}
.tcw__list {
  margin: 0;
  padding-left: 1.125rem;
  font-size: 0.75rem;
  color: #92400e;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.tcw__item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.tcw__suggestion {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
}
.tcw__swatch {
  width: 0.875rem;
  height: 0.875rem;
  border-radius: 0.25rem;
  border: 1px solid rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}
.tcw__suggestion-hex {
  font-family: monospace;
  font-size: 0.6875rem;
  color: #78350f;
}
.tcw__apply-btn {
  border: 1px solid rgba(180, 83, 9, 0.35);
  background: rgba(255, 255, 255, 0.6);
  color: #92400e;
  border-radius: 0.375rem;
  padding: 0.125rem 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  cursor: pointer;
}
.tcw__apply-btn:hover {
  background: #fff;
}
</style>
